import React, {ReactNode, useEffect} from 'react'
import {logger} from 'yougile-util'
import {Renderer, UIRegistry} from '@api/model/ui-registry'

/**
 * All possible slot names (extend this type to add new slots)
 */
export type UiSlotName =
  | 'overlay-container'
  | 'task-card-bottom'
  | 'statistics-nav-after'
  | 'statistics-page-content'
  | 'task-stickers-before'
  | 'task-stickers-after'
  | 'chat-list-header-actions-left'
  | 'chat-list-header-actions-right'
  | 'chat-messages-bottom'
  | 'chat-input-left'
  | 'chat-input-actions'
  | 'root'
  | 'profile-page-profile-bottom'
  | 'task-header-bottom'
  | 'task-info-stickers-before'
  | 'task-info-stickers-after'
  | 'sticker-panel-menu-start'
  | 'sticker-panel-menu-end'
  | 'task-chat-list-header'
  | 'before-right-panel'
  | 'system-appearance-color-scheme-after'

interface UiEntryProps {
  name: UiSlotName
  /**
   * @experimanetal
   * Data that will be passed to the component.
   * @notice In the future, it will be possible to pass data with specific types.
   * @todo Add types for data depending on the name
   */
  data?: any
}

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  override state: ErrorBoundaryState = {hasError: false}

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error
    }
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    logger.error('UiSlot caught an error:', error, errorInfo)
  }

  override render() {
    if (this.state.hasError) {
      return null
    }
    return this.props.children
  }
}

/**
 * @experimanetal
 * UI Slot allows you to render components in a specific place in the application from the extensions.
 * @example
 * UI.embedComponentToSlot('chat-input-left', () => <InputPrefix />)
 * @constructor
 */
export const UiSlot = ({name, data}: UiEntryProps) => {
  const [renderers, setRenderers] = React.useState<Renderer[]>([])

  useEffect(() => {
    return UIRegistry.registerSlot(name, setRenderers)
  }, [name])

  return (
    <ErrorBoundary>
      {renderers.map(({key, renderer: Renderer}) => (
        <Renderer key={key} {...data} />
      ))}
    </ErrorBoundary>
  )
}
